import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id", "position", "offset", "defaultOpen", "onSelect", "onToggle", "children"];
import React, { forwardRef, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { isFunction } from '../helper';
import { useId } from '../hooks';
import ScopedCssBaseline from '../StyleBase/ScopedCssBaseline';
import Dropdown from '../Dropdown';
import DropdownMenuTrigger from './DropdownMenuTrigger';
import DropdownMenuContext, { SelectableContext } from './DropdownMenuContext';
import DropdownMenuList from './DropdownMenuList';
import DropdownMenuItem from './DropdownMenuItem';
import DropdownMenuTitle from './DropdownMenuTitle';
import DropdownMenuDivider from './DropdownMenuDivider';
const Root = styled.div.withConfig({
  displayName: "DropdownMenu__Root",
  componentId: "sc-1kmdnoz-0"
})(["", " position:relative;"], ScopedCssBaseline);
const DropdownMenu = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    id,
    position,
    offset,
    defaultOpen,
    onSelect,
    onToggle,
    children
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  const dropdownRef = useRef(ref);
  const [open, setOpen] = useState(defaultOpen);
  const [triggerElement, setTriggerElement] = useState(null);
  const accessibilityId = useId(id);

  const handleToggle = event => {
    setOpen(open => !open);
    isFunction(onToggle) && onToggle(event, !open);
  };

  const handleSelect = event => {
    open && handleToggle(event);
    isFunction(onSelect) && onSelect(event);
  };

  const context = {
    id: accessibilityId,
    position,
    offset,
    isOpen: open,
    toggle: event => handleToggle(event),
    triggerElement,
    setTriggerElement
  };
  return /*#__PURE__*/React.createElement(SelectableContext.Provider, {
    value: handleSelect
  }, /*#__PURE__*/React.createElement(DropdownMenuContext.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement(Root, _extends({}, rest, {
    ref: dropdownRef,
    role: "menu"
  }), children)));
});
DropdownMenu.positions = {
  bottom: Dropdown.positions.bottom,
  bottomStart: Dropdown.positions.bottomStart,
  bottomEnd: Dropdown.positions.bottomEnd
};
DropdownMenu.displayName = 'DropdownMenu';
DropdownMenu.Trigger = DropdownMenuTrigger;
DropdownMenu.List = DropdownMenuList;
DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.Title = DropdownMenuTitle;
DropdownMenu.Divider = DropdownMenuDivider;
DropdownMenu.defaultProps = {
  defaultOpen: false,
  position: DropdownMenu.positions.bottomStart
};
process.env.NODE_ENV !== "production" ? DropdownMenu.propTypes = {
  /**
   * Sets the children element.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,

  /**
   * Responsible to connect accessibly `DropdownMenu.List` with `DropdownMenu.Trigger`. The id value is used in `aria-labelledby`.
   */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * A boolean value to control if render opened or closed.
   */
  defaultOpen: PropTypes.bool,

  /**
   * Callback fired when `DropdownMenu.Trigger` component is clicked or using accessible keyboard navigation to close `DropdownMenu.List` with `ESC`.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   * @param {boolean} newOpenState - The open new state.
   */
  onToggle: PropTypes.func,

  /**
   * Callback fired when  `DropdownMenu.Item` is clicked and the item is selected.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onSelect: PropTypes.func,

  /**
   * Determines the direction and location of the `DropdownMenu.List` in relation to `DropdownMenu.Trigger`.
   */
  position: PropTypes.oneOf([DropdownMenu.positions.bottom, DropdownMenu.positions.bottomStart, DropdownMenu.positions.bottomEnd]),

  /**
   * Sets additional distance between dropdown and trigger using number to represent pixels.
   */
  offset: PropTypes.number
} : void 0;
export default DropdownMenu;